import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { SignalDefinition } from "@/api/signals";
import { categoricalLabel } from "../lib/signalLabels";

const inputCls =
  "w-full rounded-md border border-ap-line bg-white px-2 py-1 text-sm shadow-sm focus:border-ap-primary focus:outline-none focus:ring-1 focus:ring-ap-primary";

function LabeledControl({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ap-muted">{label}</span>
      {children}
    </label>
  );
}

export interface ValueInputProps {
  defn: SignalDefinition;
  valueText: string;
  setValueText: (v: string) => void;
  valueBool: boolean;
  setValueBool: (v: boolean) => void;
  lat: string;
  setLat: (v: string) => void;
  lon: string;
  setLon: (v: string) => void;
  /** When true, drops the html `required` attribute so a template member can be left blank. */
  optional?: boolean;
}

export function ValueInput({
  defn,
  valueText,
  setValueText,
  valueBool,
  setValueBool,
  lat,
  setLat,
  lon,
  setLon,
  optional = false,
}: ValueInputProps): ReactNode {
  const { t, i18n } = useTranslation("signals");
  const req = !optional;
  if (defn.value_kind === "numeric") {
    // CS-13: non-blocking out-of-expected-range hint. The server is still
    // authoritative and a genuine out-of-range reading is allowed — this
    // only warns. No chip when both bounds are null.
    const hasBounds = defn.value_min !== null || defn.value_max !== null;
    const parsed = valueText.trim() === "" ? null : Number(valueText);
    const outOfRange =
      hasBounds &&
      parsed !== null &&
      Number.isFinite(parsed) &&
      ((defn.value_min !== null && parsed < Number(defn.value_min)) ||
        (defn.value_max !== null && parsed > Number(defn.value_max)));
    const bounds = `${defn.value_min ?? "−∞"} … ${defn.value_max ?? "∞"}`;
    return (
      <LabeledControl
        label={defn.unit ? t("log.form.valueWithUnit", { unit: defn.unit }) : t("log.form.value")}
      >
        <input
          required={req}
          inputMode="decimal"
          value={valueText}
          onChange={(e) => setValueText(e.target.value)}
          aria-invalid={outOfRange || undefined}
          className={inputCls}
        />
        {outOfRange ? (
          <span className="text-[11px] text-ap-warn">{t("log.form.outOfRange", { bounds })}</span>
        ) : null}
      </LabeledControl>
    );
  }
  if (defn.value_kind === "categorical") {
    return (
      <LabeledControl label={t("log.form.value")}>
        <select
          required={req}
          value={valueText}
          onChange={(e) => setValueText(e.target.value)}
          className={inputCls}
        >
          <option value="" disabled>
            {t("log.form.pickOne")}
          </option>
          {(defn.categorical_values ?? []).map((v) => (
            <option key={v} value={v}>
              {categoricalLabel(i18n.language, defn, v)}
            </option>
          ))}
        </select>
      </LabeledControl>
    );
  }
  if (defn.value_kind === "event") {
    return (
      <LabeledControl label={t("log.form.description")}>
        <input
          required={req}
          maxLength={500}
          value={valueText}
          onChange={(e) => setValueText(e.target.value)}
          className={inputCls}
        />
      </LabeledControl>
    );
  }
  if (defn.value_kind === "boolean") {
    return (
      <LabeledControl label={t("log.form.value")}>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={valueBool}
            onChange={(e) => setValueBool(e.target.checked)}
          />
          <span>{valueBool ? "true" : "false"}</span>
        </label>
      </LabeledControl>
    );
  }
  // geopoint
  return (
    <div className="grid grid-cols-2 gap-3">
      <LabeledControl label={t("log.form.lat")}>
        <input
          required={req}
          inputMode="decimal"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          className={inputCls}
        />
      </LabeledControl>
      <LabeledControl label={t("log.form.lon")}>
        <input
          required={req}
          inputMode="decimal"
          value={lon}
          onChange={(e) => setLon(e.target.value)}
          className={inputCls}
        />
      </LabeledControl>
    </div>
  );
}
